import { type CampaignDraftInput, CHANNEL_LABELS, formatInt, formatMXN, MESSAGE_LIMITS } from "@cafe/shared";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, History, Megaphone, X } from "lucide-react";
import { api, unwrap } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Badge, Button, Card, Spinner } from "../ui/primitives";
import { ReachFunnel } from "./ReachFunnel";
import { SourceList } from "./SourceList";

/**
 * Approval gate #1 (nothing is created until the owner approves): the agent proposed a draft;
 * we show audience, real reach per channel, offer + cost, expected impact, similar past campaigns
 * and the message, computed by the same evaluate_campaign function the MCP exposes.
 */
export function ProposalCard({
  input: raw,
  onRespond,
  responded,
}: {
  input: CampaignDraftInput & { summary?: string };
  onRespond?: (approved: boolean) => void;
  responded?: boolean | null;
}) {
  const { summary, ...input } = raw;
  const evaluation = useQuery({
    queryKey: ["evaluate", JSON.stringify(input)],
    queryFn: async () => unwrap(await api.campaigns.evaluate.$post({ json: input })),
    staleTime: 60_000,
  });
  const e = evaluation.data;
  const len = [...input.message].length;
  const limit = MESSAGE_LIMITS[input.channel];

  return (
    <Card className="overflow-hidden border-caramel/60">
      <div className="flex items-start justify-between gap-2 border-b border-latte bg-amber-50 px-3 py-2">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Megaphone className="size-4" /> Propuesta: {input.name}
          </p>
          <p className="text-xs text-roast/80">{input.objective}</p>
        </div>
        <Badge tone="warn">Pendiente de tu aprobación</Badge>
      </div>
      {summary && <p className="border-b border-latte px-3 py-2 text-sm leading-relaxed">{summary}</p>}
      {evaluation.isLoading && (
        <p className="flex items-center gap-2 p-3 text-xs text-roast/80">
          <Spinner className="size-3" /> Calculando alcance real e impacto…
        </p>
      )}
      {e && (
        <div className="grid gap-3 p-3 sm:grid-cols-2">
          <div className="space-y-2 text-xs">
            <Field label="Audiencia">{e.audience_description}</Field>
            {e.chosen_reach && <ReachFunnel reach={e.chosen_reach} />}
            <p className="text-[11px] text-roast/70">
              Otros canales:{" "}
              {e.reach_by_channel
                .filter((r) => r.channel !== input.channel)
                .map((r) => `${CHANNEL_LABELS[r.channel]} ${formatInt(r.reachable)}`)
                .join(" · ")}
            </p>
            <Field label="Oferta">{e.offer_description}</Field>
            {e.cost_per_redemption !== null && (
              <Field label="Costo por canje">
                {formatMXN(e.cost_per_redemption)} <span className="font-normal text-roast/70">{e.cost_note}</span>
              </Field>
            )}
            <div className="rounded-xl bg-crema p-2">
              <p className="text-[11px] font-medium">Impacto esperado</p>
              <p className="tabular-nums">
                {e.expected.redemptions !== null ? `~${formatInt(e.expected.redemptions)} canjes` : "Sin estimación de canjes"}
                {e.expected.offer_cost !== null ? ` · ~${formatMXN(e.expected.offer_cost)} de costo` : ""}
                {e.expected.extra_visitors !== null ? ` · ~${formatInt(e.expected.extra_visitors)} visitas extra vs. no enviar` : ""}
              </p>
              <p className="text-[11px] text-roast/70">{e.expected.basis}</p>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium text-roast/80">Mensaje · {CHANNEL_LABELS[input.channel]}</p>
            <div className="rounded-2xl bg-[#e7f7e3] p-3 text-sm leading-relaxed whitespace-pre-wrap">{input.message}</div>
            <p className={cn("text-right text-[11px] tabular-nums", len > limit ? "text-red-700" : "text-roast/70")}>
              {len}/{limit} caracteres
            </p>
            {e.similar_campaigns.length > 0 && (
              <div className="text-[11px]">
                <p className="flex items-center gap-1 font-medium">
                  <History className="size-3" /> Campañas parecidas
                </p>
                {e.similar_campaigns.map((s) => (
                  <p key={s.id} className="text-roast/80">
                    {s.name} ({s.similarity.join(", ")}): canje {s.redemption_rate_pct ?? "—"}%
                    {s.lift_pp !== null ? `, lift +${s.lift_pp} pp` : ", sin control"}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {e && (e.problems.length > 0 || e.warnings.length > 0) && (
        <div className="space-y-1 px-3 pb-2 text-[11px]">
          {e.problems.map((p) => (
            <p key={p} className="flex gap-1 text-red-700">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" /> {p}
            </p>
          ))}
          {e.warnings.map((w) => (
            <p key={w} className="flex gap-1 text-amber-800">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" /> {w}
            </p>
          ))}
        </div>
      )}
      {e && (
        <div className="px-3">
          <SourceList sources={e.sources} />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-latte px-3 py-2">
        {responded === true && <p className="text-xs text-emerald-800">Aprobaste la propuesta: creando el borrador…</p>}
        {responded === false && <p className="text-xs text-roast/80">Rechazaste la propuesta. Dime qué cambiar.</p>}
        {responded == null && onRespond && (
          <>
            <Button variant="secondary" onClick={() => onRespond(false)}>
              <X className="size-4" /> No, ajustar
            </Button>
            <Button onClick={() => onRespond(true)} disabled={!e || e.problems.length > 0}>
              <Check className="size-4" /> Aprobar y crear borrador
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] text-roast/70">{label}</p>
      <p className="font-medium">{children}</p>
    </div>
  );
}
